import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { EVENT_CATEGORIES, PROFESSIONAL_ROLES, SOCIAL_LIFE_ROLES } from "../lib/constants";
import AgendaPro from "./AgendaPro";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { CalendarDays, ChevronLeft, ChevronRight, Plus, Trash2 } from "lucide-react";

function pad(n) { return String(n).padStart(2, "0"); }

export default function Agenda() {
  const { user } = useAuth();
  if (user && [...PROFESSIONAL_ROLES, ...SOCIAL_LIFE_ROLES].includes(user.role)) return <AgendaPro />;
  return <AgendaCRM />;
}

function AgendaCRM() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const ro = user?.role === "ceo";
  const DAYS = t("agenda.days", { returnObjects: true });
  const MONTHS = t("agenda.months", { returnObjects: true });
  const locale = { fr: "fr-BE", en: "en-GB", nl: "nl-BE" }[i18n.language] || "fr-BE";
  const [events, setEvents] = useState([]);
  const [cursor, setCursor] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", date: "", time: "", category: "rdv", notes: "" });

  const load = async () => { const { data } = await api.get("/events"); setEvents(data); };
  useEffect(() => { load(); }, []);

  const firstDay = new Date(cursor.y, cursor.m, 1);
  const startOffset = (firstDay.getDay() + 6) % 7; // Monday=0
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const todayISO = new Date().toISOString().slice(0, 10);

  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(`${cursor.y}-${pad(cursor.m + 1)}-${pad(d)}`);

  const evByDate = events.reduce((acc, e) => { (acc[e.date] = acc[e.date] || []).push(e); return acc; }, {});

  const move = (dir) => setCursor((c) => {
    let m = c.m + dir, y = c.y;
    if (m < 0) { m = 11; y--; } if (m > 11) { m = 0; y++; }
    return { y, m };
  });

  const openNew = (date) => { setForm({ title: "", date: date || todayISO, time: "", category: "rdv", notes: "" }); setOpen(true); };
  const save = async () => {
    if (!form.title.trim() || !form.date) return toast.error(t("agenda.errTitleDate"));
    await api.post("/events", form);
    setOpen(false); toast.success(t("agenda.added")); load();
  };
  const remove = async (e) => { await api.delete(`/events/${e.id}`); load(); };

  const upcoming = [...events].filter((e) => e.date >= todayISO).sort((a, b) => (a.date + (a.time || "")).localeCompare(b.date + (b.time || ""))).slice(0, 8);

  return (
    <div className="p-5 sm:p-8 max-w-[1400px] mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div data-tour="agenda-header">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-blue-300">{t("agenda.tag")}</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-1 flex items-center gap-3">
            <span className="h-11 w-11 rounded-2xl flex items-center justify-center bg-blue-500/20 text-blue-400"><CalendarDays className="h-6 w-6" /></span>
            {t("agenda.title")}
          </h1>
          <p className="text-muted-foreground mt-1">{t("agenda.subtitle")}</p>
        </div>
        {!ro && <Button className="h-11" onClick={() => openNew()} data-testid="add-event" data-tour="agenda-add"><Plus className="h-4 w-4 mr-2" /> {t("agenda.addEvent")}</Button>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <div className="lg:col-span-2 glass rounded-2xl card-3d p-4" data-tour="agenda-cal">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg font-bold">{MONTHS[cursor.m]} {cursor.y}</h2>
            <div className="flex gap-1">
              <button onClick={() => move(-1)} data-testid="prev-month" className="h-9 w-9 rounded-lg hover:bg-white/10 flex items-center justify-center"><ChevronLeft className="h-5 w-5" /></button>
              <button onClick={() => move(1)} data-testid="next-month" className="h-9 w-9 rounded-lg hover:bg-white/10 flex items-center justify-center"><ChevronRight className="h-5 w-5" /></button>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1.5">
            {DAYS.map((d) => <div key={d} className="text-center text-xs font-semibold text-muted-foreground py-1">{d}</div>)}
            {cells.map((date, i) => (
              <div key={i} className={`min-h-[84px] rounded-xl p-1.5 border ${date ? "border-white/10 hover:bg-white/5 cursor-pointer" : "border-transparent"} ${date === todayISO ? "ring-2 ring-blue-400/60 bg-blue-500/10" : ""}`}
                onClick={() => date && !ro && openNew(date)} data-testid={date ? `day-${date}` : undefined}>
                {date && <div className="text-xs font-semibold text-muted-foreground">{parseInt(date.slice(-2), 10)}</div>}
                <div className="space-y-1 mt-1">
                  {(evByDate[date] || []).slice(0, 3).map((e) => (
                    <div key={e.id} className="text-[10px] truncate rounded px-1 py-0.5 text-white" style={{ background: (EVENT_CATEGORIES[e.category]?.color || "#6366f1") + "cc" }}>
                      {e.time ? e.time + " " : ""}{e.title}
                    </div>
                  ))}
                  {(evByDate[date] || []).length > 3 && <div className="text-[10px] text-muted-foreground">+{evByDate[date].length - 3}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="glass rounded-2xl card-3d p-5">
          <h3 className="font-display font-bold mb-3">{t("agenda.upcoming")}</h3>
          <div className="space-y-2.5">
            {upcoming.length === 0 && <p className="text-sm text-muted-foreground py-4">{t("agenda.noUpcoming")}</p>}
            {upcoming.map((e) => (
              <div key={e.id} className="flex items-start gap-3 group" data-testid={`event-${e.id}`}>
                <span className="h-2.5 w-2.5 rounded-full mt-1.5 shrink-0" style={{ background: EVENT_CATEGORIES[e.category]?.color }} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium leading-tight">{e.title}</p>
                  <p className="text-xs text-muted-foreground">{new Date(e.date).toLocaleDateString(locale, { day: "numeric", month: "short" })}{e.time ? ` · ${e.time}` : ""} · {t(`agenda.cat.${e.category}`, { defaultValue: EVENT_CATEGORIES[e.category]?.label })}</p>
                </div>
                {!ro && <button onClick={() => remove(e)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-400 transition"><Trash2 className="h-4 w-4" /></button>}
              </div>
            ))}
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">{t("agenda.newEvent")}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>{t("agenda.fTitle")}</Label><Input data-testid="event-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2"><Label>{t("agenda.fDate")}</Label><Input type="date" data-testid="event-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
              <div className="space-y-2"><Label>{t("agenda.fTime")}</Label><Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} /></div>
              <div className="space-y-2"><Label>{t("agenda.fCategory")}</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.keys(EVENT_CATEGORIES).map((k) => <SelectItem key={k} value={k}>{t(`agenda.cat.${k}`, { defaultValue: EVENT_CATEGORIES[k].label })}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2"><Label>{t("agenda.fNotes")}</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="event-save">{t("agenda.save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
